import { Link, useParams } from "react-router-dom";
import { Building, CalendarDays, Image as ImageIcon, MapPin, Navigation } from "lucide-react";
import Seo from "@/components/site/Seo";
import { EmptyState, PageHero, Reveal, SectionHeading, StatusPill } from "@/components/site/kit";
import { MatchCard, indexBy } from "@/components/site/cards";
import useFetch from "@/hooks/useFetch";
import { fileUrl } from "@/lib/api";
import NotFound from "./NotFound";

export function Venues() {
  const { data: venues } = useFetch("/venues", []);
  return (
    <>
      <Seo title="Venues" description="Host cities of the Telangana–Andhra T10 League: Hyderabad, Telangana and Amaravati, Andhra Pradesh. Venue announcements coming soon." />
      <PageHero testid="venues-hero" eyebrow="Venues" title="Two Host Cities" image="/images/hyderabad-night.jpg">
        One venue in each city. Venue names and details are pending official confirmation.
      </PageHero>
      <section className="container-x grid gap-6 py-20 md:grid-cols-2">
        {(venues || []).map((v, i) => (
          <Reveal key={v.id} delay={i * 0.1}>
            <Link to={`/venues/${v.slug}`} data-testid={`venue-card-${v.slug}`} className="card-lift group block overflow-hidden rounded-md border border-white/10 bg-[#112240]">
              <div className="aspect-[16/10] overflow-hidden"><img src={fileUrl(v.image)} alt={`${v.city}, ${v.state}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
              <div className="p-6">
                <StatusPill tone={v.confirmed ? "ok" : "soon"}>{v.confirmed ? "Venue confirmed" : "Venue announcement coming soon"}</StatusPill>
                <h2 className="mt-4 font-display text-4xl uppercase">{v.city}</h2>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#00E5FF]">{v.state}</p>
                <p className="mt-3 line-clamp-3 text-sm text-slate-400">{v.intro}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </section>
    </>
  );
}

const Block = ({ icon: Icon, title, text, fallback, testid }) => (
  <div data-testid={testid} className="rounded-md border border-white/10 bg-[#112240] p-6">
    <Icon className="h-6 w-6 text-[#00E5FF]" />
    <h3 className="mt-4 font-display text-2xl uppercase">{title}</h3>
    <p className="mt-2 whitespace-pre-line text-sm text-slate-400">{text || fallback}</p>
  </div>
);

export function VenueDetail() {
  const { slug } = useParams();
  const { data: v, loading, error } = useFetch(`/venues/${slug}`);
  const { data: teams } = useFetch("/teams", []);
  const { data: venues } = useFetch("/venues", []);
  if (error) return <NotFound />;
  if (loading || !v) return <div className="min-h-screen" />;
  return (
    <>
      <Seo title={`${v.city} Venue`} description={`${v.city}, ${v.state} — host city of the Telangana–Andhra T10 League. Venue details coming soon.`} image={v.image} />
      <PageHero testid="venue-hero" eyebrow={`Host City · ${v.state}`} title={v.city} image={fileUrl(v.image)}>{v.intro}</PageHero>
      <section className="container-x py-16">
        <SectionHeading eyebrow="Venue" title={v.venue_name || "Venue to be announced"} />
        {v.address && <p className="mt-4 flex items-center gap-2 text-slate-300"><MapPin className="h-4 w-4" />{v.address}</p>}
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          <Block testid="venue-facilities" icon={Building} title="Facilities" text={v.facilities} fallback="Facility details will be published once the venue is confirmed." />
          <Block testid="venue-directions" icon={Navigation} title="Directions" text={v.directions} fallback="Directions and transport information will be published once the venue is confirmed." />
          <Block testid="venue-matchday" icon={CalendarDays} title="Matchday Information" text={v.matchday_info} fallback="Matchday timings, entry and ticketing information will be announced closer to the season." />
        </div>
      </section>
      <section className="container-x pb-12">
        <h2 className="mb-6 font-display text-3xl uppercase">Matches in {v.city}</h2>
        {v.matches?.length ? <div className="grid gap-4 md:grid-cols-2">{v.matches.map((m) => <MatchCard key={m.id} match={m} teams={indexBy(teams)} venues={indexBy(venues)} />)}</div>
          : <EmptyState testid="venue-matches-empty" icon={CalendarDays} title="Fixtures will be announced soon" />}
      </section>
      <section className="container-x pb-24">
        <h2 className="mb-6 font-display text-3xl uppercase">Gallery</h2>
        {v.gallery?.length ? <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{v.gallery.map((g) => <img key={g.id} src={fileUrl(g.image)} alt={g.title} loading="lazy" className="aspect-square w-full rounded-md object-cover" />)}</div>
          : <EmptyState testid="venue-gallery-empty" icon={ImageIcon} title="Gallery coming soon" />}
      </section>
    </>
  );
}
